import React, { useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { AsciiField, PixelLogo, ScrambleText } from "@rinci/brand/react";
import { useForm } from "@tanstack/react-form";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ActivityIcon,
  AlertTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  Clock3Icon,
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  KeyRoundIcon,
  LogOutIcon,
  MailIcon,
  PowerIcon,
  RefreshCwIcon,
  RepeatIcon,
  SearchIcon,
  SendIcon,
  ShieldCheckIcon,
  UserPlusIcon,
  UsersIcon,
  type LucideIcon,
} from "lucide-react";
import {
  dashboardPageFromPath,
  dashboardPagePath,
  dashboardPages,
  type DashboardPage,
} from "./dashboard/routes";
import {
  api,
  ApiClient,
  type AppLog,
  type AuthSession,
  type DashboardAnalytics,
  type DashboardApiKey,
  type DashboardScope,
  type DashboardUser,
  type EmailEvent,
  type ProvidersConfigInput,
  type ProviderStatus,
  type RenewApiKeyResponse,
  type UsageEvent,
} from "./api";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { TooltipProvider } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Landing, signInPath } from "./landing";
import "./styles.css";

const sessionKey = "rinci-hub-token";
const numberFormatter = new Intl.NumberFormat("en");
const percentFormatter = new Intl.NumberFormat("en", {
  maximumFractionDigits: 1,
  style: "percent",
});
const queryClient = new QueryClient();
const tablePageSize = 50;
const maxTableLimit = 200;

function formatDate(value: string | null) {
  if (!value) return "Never";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatCount(value: number) {
  return numberFormatter.format(value);
}

function formatPercent(value: number) {
  return percentFormatter.format(value);
}

function canManage(session: AuthSession) {
  return (
    session.scopes.includes("*") ||
    session.user.role === "service" ||
    session.user.role === "admin" ||
    session.user.role === "manager"
  );
}

function statusVariant(
  value: "good" | "warn" | "neutral" | "bad",
): React.ComponentProps<typeof Badge>["variant"] {
  if (value === "bad") return "destructive";
  if (value === "neutral") return "secondary";
  return "outline";
}

const statusToneClass: Record<"good" | "warn" | "neutral" | "bad", string> = {
  good: "border-signal/40 text-signal",
  warn: "border-paper/30 text-paper",
  neutral: "text-dim",
  bad: "",
};

function StatusBadge({
  tone,
  children,
}: {
  tone: "good" | "warn" | "neutral" | "bad";
  children: React.ReactNode;
}) {
  return (
    <Badge
      className={cn("shrink-0", statusToneClass[tone])}
      variant={statusVariant(tone)}
    >
      {children}
    </Badge>
  );
}

function BrandLogo() {
  return (
    <span className="flex size-8 shrink-0 items-center justify-center">
      <PixelLogo label="Rinci Hub" mark width={22} />
    </span>
  );
}

function brandDelay(ms: number) {
  return { "--brand-delay": `${ms}ms` } as React.CSSProperties;
}

function TableFrame({ children, className }: React.ComponentProps<"div">) {
  return (
    <div className={cn("min-w-0 overflow-hidden rounded-lg border", className)}>
      {children}
    </div>
  );
}

function EmptyTableRow({
  colSpan,
  children,
}: {
  colSpan: number;
  children: React.ReactNode;
}) {
  return (
    <TableRow>
      <TableCell className="text-muted-foreground" colSpan={colSpan}>
        {children}
      </TableCell>
    </TableRow>
  );
}

function DetailDialogContent({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-2xl">
      {children}
    </DialogContent>
  );
}

function emailStatusTone(
  status: EmailEvent["status"],
): "good" | "warn" | "neutral" | "bad" {
  if (status === "failed") return "bad";
  if (status === "processing") return "warn";
  if (status === "queued") return "neutral";
  return "good";
}

function LoginPanel({ onLogin }: { onLogin: (session: AuthSession) => void }) {
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    defaultValues: { token: "" },
    onSubmit: async ({ value }) => {
      const token = value.token.trim();
      setError(null);
      try {
        const client = api.withToken(token);
        const result = await client.login(token);
        sessionStorage.setItem(sessionKey, token);
        onLogin({
          token,
          user: result.user,
          scopes: result.auth.scopes,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Login failed");
      }
    },
  });

  return (
    <main className="grid min-h-[100dvh] bg-ink lg:grid-cols-[minmax(0,1fr)_480px]">
      <section className="relative isolate hidden overflow-hidden border-r border-line p-10 lg:flex lg:flex-col lg:justify-between">
        <div className="ascii-backdrop absolute inset-0 -z-10">
          <AsciiField />
        </div>
        <a
          aria-label="Rinci Hub home"
          className="brand-in flex w-fit items-center gap-3"
          href="#/"
          style={brandDelay(60)}
        >
          <PixelLogo label="Rinci" width={82} />
          <span className="pixel-type text-[11px] text-signal">Hub</span>
        </a>
        <div className="brand-in max-w-xl" style={brandDelay(160)}>
          <p className="pixel-type mb-4 text-[11px] text-faint">
            Transactional email API
          </p>
          <h1 className="max-w-[18ch] text-[40px] leading-[1.08] font-medium tracking-tight text-balance">
            Sends, API usage and failed deliveries in one place.
          </h1>
        </div>
        <dl className="brand-in grid max-w-xl" style={brandDelay(260)}>
          {[
            ["Email API", "Queue requests and inspect delivery state"],
            ["Provider routing", "Manage SMTP, Zoho, and Resend failover"],
            ["Audit logs", "Track API errors and failed sends"],
          ].map(([label, value]) => (
            <div
              className="flex items-center justify-between gap-4 border-t border-line py-3"
              key={label}
            >
              <dt className="pixel-type text-[11px] text-paper">{label}</dt>
              <dd className="text-right text-sm text-dim">{value}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section className="flex items-center justify-center px-4 py-10 sm:p-8">
        <div className="brand-in w-full max-w-sm" style={brandDelay(120)}>
          <a
            aria-label="Rinci Hub home"
            className="mb-10 flex w-fit items-center gap-3 lg:hidden"
            href="#/"
          >
            <PixelLogo label="Rinci" width={72} />
            <span className="pixel-type text-[11px] text-signal">Hub</span>
          </a>
          <p className="pixel-type text-[11px] text-faint">Sign in</p>
          <h2 className="mt-3 text-[28px] leading-tight font-medium tracking-tight">
            Rinci Hub
          </h2>
          <p className="mt-2 text-sm text-dim">
            Sign in with a service key or generated Hub API key.
          </p>
          <div className="mt-8">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void form.handleSubmit();
              }}
            >
              <FieldGroup>
                <form.Field
                  name="token"
                  validators={{
                    onChange: ({ value }) =>
                      value.trim().length >= 16
                        ? undefined
                        : "Enter a valid token.",
                  }}
                >
                  {(field) => (
                    <Field data-invalid={field.state.meta.errors.length > 0}>
                      <FieldLabel htmlFor={field.name}>API token</FieldLabel>
                      <Input
                        className="h-10 font-mono text-sm"
                        id={field.name}
                        type="password"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(event) =>
                          field.handleChange(event.target.value)
                        }
                        placeholder="hub_... or service key"
                        autoComplete="current-password"
                        aria-invalid={field.state.meta.errors.length > 0}
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </Field>
                  )}
                </form.Field>
                {error ? (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                ) : null}
                <form.Subscribe
                  selector={(state) => [
                    state.canSubmit,
                    state.isSubmitting,
                    state.values.token,
                  ]}
                >
                  {([canSubmit, isSubmitting, token]) => {
                    const tokenValue = String(token);
                    return (
                      <Button
                        className="pixel-type h-10 text-[12px]"
                        type="submit"
                        disabled={
                          !canSubmit ||
                          Boolean(isSubmitting) ||
                          tokenValue.trim().length < 16
                        }
                      >
                        <KeyRoundIcon data-icon="inline-start" />
                        {isSubmitting ? "Checking..." : "Open dashboard"}
                      </Button>
                    );
                  }}
                </form.Subscribe>
              </FieldGroup>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}

function MetricCard({
  label,
  value,
  hint,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  hint: string;
  icon: LucideIcon;
}) {
  return (
    <Card size="sm" className="shadow-none">
      <CardHeader className="grid-cols-[1fr_auto]">
        <div className="min-w-0">
          <CardDescription className="pixel-type text-[10.5px] text-faint">
            {label}
          </CardDescription>
          <CardTitle className="mt-3 text-3xl font-medium tabular-nums tracking-tight group-data-[size=sm]/card:text-3xl">
            {value}
          </CardTitle>
        </div>
        <Icon aria-hidden="true" className="size-4 text-faint" />
      </CardHeader>
      <CardContent>
        <p className="text-sm text-dim">{hint}</p>
      </CardContent>
    </Card>
  );
}

function BucketList({
  title,
  items,
}: {
  title: string;
  items: Array<{ name: string; count: number }>;
}) {
  const max = Math.max(...items.map((item) => item.count), 1);

  return (
    <Card className="shadow-none">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {items.map((item) => (
          <div className="grid gap-2" key={item.name}>
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="truncate font-medium">{item.name}</span>
              <span className="tabular-nums text-muted-foreground">
                {formatCount(item.count)}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden bg-line">
              <div
                className="h-full bg-signal"
                style={{ width: `${Math.max(6, (item.count / max) * 100)}%` }}
              />
            </div>
          </div>
        ))}
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No records in this range.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function TopRoutesCard({
  routes,
}: {
  routes: DashboardAnalytics["topRoutes"];
}) {
  return (
    <Card className="shadow-none">
      <CardHeader>
        <CardTitle>Top routes</CardTitle>
        <CardDescription>
          Highest traffic protected routes in the last 24 hours
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <TableFrame className="hidden md:block">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[55%]">Route</TableHead>
                <TableHead className="w-[15%]">Requests</TableHead>
                <TableHead className="w-[15%]">Errors</TableHead>
                <TableHead className="w-[15%]">Avg duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {routes.map((route) => (
                <TableRow key={`${route.method}:${route.path}`}>
                  <TableCell className="min-w-0">
                    <div className="min-w-0">
                      <p className="font-medium">{route.method}</p>
                      <p className="truncate text-sm text-muted-foreground">
                        {route.path}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>{formatCount(route.requests)}</TableCell>
                  <TableCell>{formatCount(route.errors)}</TableCell>
                  <TableCell>{formatCount(route.averageDurationMs)}ms</TableCell>
                </TableRow>
              ))}
              {routes.length === 0 ? (
                <EmptyTableRow colSpan={4}>
                  No route activity in this range.
                </EmptyTableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableFrame>
        <div className="grid gap-2 md:hidden">
          {routes.map((route) => (
            <div
              className="grid gap-2 rounded-lg border bg-background p-3"
              key={`${route.method}:${route.path}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium">{route.method}</span>
                <span className="text-sm text-muted-foreground">
                  {formatCount(route.requests)} requests
                </span>
              </div>
              <p className="truncate text-sm text-muted-foreground">
                {route.path}
              </p>
              <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                <span>{formatCount(route.errors)} errors</span>
                <span>{formatCount(route.averageDurationMs)}ms avg</span>
              </div>
            </div>
          ))}
          {routes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No route activity in this range.
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function AnalyticsPanel({ analytics }: { analytics: DashboardAnalytics }) {
  return (
    <div className="grid gap-4">
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="24h requests"
          value={formatCount(analytics.totals.requests)}
          hint={`${formatCount(analytics.totals.errors)} errors recorded`}
          icon={ActivityIcon}
        />
        <MetricCard
          label="Failure rate"
          value={formatPercent(analytics.totals.failureRate)}
          hint="HTTP 4xx/5xx over total requests"
          icon={AlertTriangleIcon}
        />
        <MetricCard
          label="Average latency"
          value={`${formatCount(analytics.totals.averageDurationMs)}ms`}
          hint="Mean protected route duration"
          icon={Clock3Icon}
        />
        <MetricCard
          label="Active users"
          value={formatCount(analytics.totals.activeUsers)}
          hint="Distinct users with API traffic"
          icon={UsersIcon}
        />
      </section>

      <section className="grid gap-4 xl:grid-cols-3">
        <BucketList
          title="Requests by status"
          items={analytics.requestsByStatus}
        />
        <BucketList title="Emails by status" items={analytics.emailsByStatus} />
        <BucketList
          title="Emails by provider"
          items={analytics.emailsByProvider}
        />
      </section>

      <TopRoutesCard routes={analytics.topRoutes} />
    </div>
  );
}

function ScopeSwitch({
  scope,
  disabled,
  onChange,
}: {
  scope: DashboardScope;
  disabled: boolean;
  onChange: (scope: DashboardScope) => void;
}) {
  return (
    <ToggleGroup
      aria-label="Dashboard data scope"
      className="rounded-md border bg-background p-0.5"
      onValueChange={(value) => {
        if (value === "mine" || value === "all") onChange(value);
      }}
      size="sm"
      spacing={0}
      type="single"
      value={scope}
      variant="default"
    >
      <ToggleGroupItem value="mine">Mine</ToggleGroupItem>
      <ToggleGroupItem disabled={disabled} value="all">
        All
      </ToggleGroupItem>
    </ToggleGroup>
  );
}

function ProviderRail({
  providers,
  manager,
  pending,
  onUpdate,
}: {
  providers: ProviderStatus[];
  manager: boolean;
  pending: boolean;
  onUpdate: (config: ProvidersConfigInput) => void;
}) {
  // Render in routing order; disabled providers sort last.
  const ordered = [...providers].sort((a, b) => a.order - b.order);
  const enabledNames = ordered
    .filter((provider) => provider.role !== "disabled")
    .map((provider) => provider.name);

  function toggle(provider: ProviderStatus) {
    const enabled = provider.role === "disabled";
    onUpdate({ enabled: { [provider.name]: enabled } });
  }

  function move(provider: ProviderStatus, direction: -1 | 1) {
    const index = enabledNames.indexOf(provider.name);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= enabledNames.length) return;
    const next = [...enabledNames];
    [next[index], next[target]] = [next[target], next[index]];
    onUpdate({ order: next });
  }

  return (
    <Card className="shadow-none">
      <CardHeader>
        <CardTitle>Providers</CardTitle>
        <CardDescription>
          {manager
            ? "Toggle providers and set routing order"
            : "Routing order and readiness"}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {ordered.map((provider) => {
          const isEnabled = provider.role !== "disabled";
          const enabledIndex = enabledNames.indexOf(provider.name);
          return (
            <div
              className="flex items-center justify-between gap-3 rounded-lg border p-3"
              key={provider.name}
            >
              <div className="min-w-0">
                <p className="font-semibold uppercase">{provider.name}</p>
                <p className="truncate text-sm text-muted-foreground">
                  {provider.host ?? provider.endpoint ?? "Not configured"}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge
                  tone={
                    !isEnabled
                      ? "neutral"
                      : provider.configured
                        ? "good"
                        : "warn"
                  }
                >
                  {provider.role}
                </StatusBadge>
                {manager ? (
                  <>
                    {isEnabled ? (
                      <div className="flex flex-col">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-5 w-6"
                          aria-label={`Move ${provider.name} up`}
                          disabled={pending || enabledIndex <= 0}
                          onClick={() => move(provider, -1)}
                        >
                          <ChevronUpIcon className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-5 w-6"
                          aria-label={`Move ${provider.name} down`}
                          disabled={
                            pending || enabledIndex >= enabledNames.length - 1
                          }
                          onClick={() => move(provider, 1)}
                        >
                          <ChevronDownIcon className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : null}
                    <Button
                      size="icon"
                      variant={isEnabled ? "secondary" : "ghost"}
                      aria-label={`${isEnabled ? "Disable" : "Enable"} ${provider.name}`}
                      title={isEnabled ? "Disable provider" : "Enable provider"}
                      disabled={pending}
                      onClick={() => toggle(provider)}
                    >
                      <PowerIcon className="h-4 w-4" />
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function RegisterUserPanel({
  client,
  disabled,
  onRegistered,
}: {
  client: ApiClient;
  disabled: boolean;
  onRegistered: () => Promise<void>;
}) {
  const [result, setResult] = useState<{
    email: string;
    token: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    defaultValues: {
      email: "",
      name: "",
      sendScope: true,
      taskScope: false,
    },
    onSubmit: async ({ value, formApi }) => {
      setResult(null);
      setError(null);
      const scopes = ["user"];
      if (value.sendScope) scopes.push("emails:send");
      if (value.taskScope) scopes.push("tasks:enqueue");
      const response = await client.registerUser({
        email: value.email.trim(),
        name: value.name.trim() || undefined,
        scopes,
      });
      setResult({
        email: response.user.email,
        token: response.apiKey.token.trim(),
      });
      formApi.reset();
      await onRegistered();
    },
  });

  return (
    <Card className="shadow-none">
      <CardHeader>
        <CardTitle>Register user</CardTitle>
        <CardDescription>
          Service-key and manager-only API issuance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit().catch((err) => {
              setResult(null);
              setError(
                err instanceof Error ? err.message : "Registration failed",
              );
            });
          }}
        >
          <FieldGroup>
            <div className="grid gap-3 sm:grid-cols-2">
              <form.Field
                name="email"
                validators={{
                  onChange: ({ value }) =>
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                      ? undefined
                      : "Use a valid email address.",
                }}
              >
                {(field) => (
                  <Field data-invalid={field.state.meta.errors.length > 0}>
                    <FieldLabel htmlFor={field.name}>Email</FieldLabel>
                    <Input
                      id={field.name}
                      disabled={disabled}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      placeholder="name@example.com"
                      aria-invalid={field.state.meta.errors.length > 0}
                    />
                    <FieldError errors={field.state.meta.errors} />
                  </Field>
                )}
              </form.Field>
              <form.Field name="name">
                {(field) => (
                  <Field>
                    <FieldLabel htmlFor={field.name}>Name</FieldLabel>
                    <Input
                      id={field.name}
                      disabled={disabled}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      placeholder="Optional"
                    />
                  </Field>
                )}
              </form.Field>
            </div>
            <FieldSet>
              <FieldLegend variant="label">Scopes</FieldLegend>
              <div className="grid gap-3 sm:grid-cols-2">
                <form.Field name="sendScope">
                  {(field) => (
                    <Field orientation="horizontal">
                      <Checkbox
                        checked={field.state.value}
                        disabled={disabled}
                        onCheckedChange={(checked) =>
                          field.handleChange(checked === true)
                        }
                      />
                      <div>
                        <FieldLabel>Email sending</FieldLabel>
                        <FieldDescription>
                          Allow POST /api/emails.
                        </FieldDescription>
                      </div>
                    </Field>
                  )}
                </form.Field>
                <form.Field name="taskScope">
                  {(field) => (
                    <Field orientation="horizontal">
                      <Checkbox
                        checked={field.state.value}
                        disabled={disabled}
                        onCheckedChange={(checked) =>
                          field.handleChange(checked === true)
                        }
                      />
                      <div>
                        <FieldLabel>Task enqueue</FieldLabel>
                        <FieldDescription>
                          Allow scheduled task creation.
                        </FieldDescription>
                      </div>
                    </Field>
                  )}
                </form.Field>
              </div>
            </FieldSet>
            <form.Subscribe
              selector={(state) => [
                state.canSubmit,
                state.isSubmitting,
                state.values.email,
              ]}
            >
              {([canSubmit, isSubmitting, email]) => {
                const emailValue = String(email);
                return (
                  <Button
                    type="submit"
                    disabled={
                      disabled ||
                      !canSubmit ||
                      Boolean(isSubmitting) ||
                      !emailValue.trim()
                    }
                    className="w-full sm:w-fit"
                  >
                    <UserPlusIcon data-icon="inline-start" />
                    {isSubmitting ? "Creating..." : "Create user and key"}
                  </Button>
                );
              }}
            </form.Subscribe>
            {result ? (
              <Alert>
                <AlertDescription className="grid gap-2">
                  <span>Created {result.email}.</span>
                  <div className="flex min-w-0 items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded-md bg-muted px-2 py-1 font-mono text-xs">
                      {result.token}
                    </code>
                    <CopyButton
                      value={result.token}
                      label="Copy API key"
                      size="sm"
                      variant="outline"
                    >
                      <CopyIcon data-icon="inline-start" />
                      Copy API key
                    </CopyButton>
                  </div>
                </AlertDescription>
              </Alert>
            ) : null}
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </FieldGroup>
        </form>
      </CardContent>
    </Card>
  );
}

function SendEmailPanel({
  client,
  onSent,
}: {
  client: ApiClient;
  onSent: () => Promise<void>;
}) {
  const [status, setStatus] = useState<string | null>(null);
  const form = useForm({
    defaultValues: {
      to: "",
      subject: "Rinci Hub email API test",
      body: "Sent from the Rinci Hub dashboard.",
      html: false,
    },
    onSubmit: async ({ value }) => {
      setStatus(null);
      const result = await client.sendEmail({
        from: "Rinci Hub <hello@mail.rin.ci>",
        to: value.to,
        subject: value.subject,
        ...(value.html ? { html: value.body } : { text: value.body }),
      });
      setStatus(`Queued ${result.id}`);
      await onSent();
    },
  });

  return (
    <Card className="shadow-none">
      <CardHeader>
        <CardTitle>Send email</CardTitle>
        <CardDescription>Resend-compatible POST /api/emails</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit().catch((err) => {
              setStatus(err instanceof Error ? err.message : "Send failed");
            });
          }}
        >
          <FieldGroup>
            <div className="grid gap-3 sm:grid-cols-2">
              <form.Field
                name="to"
                validators={{
                  onChange: ({ value }) =>
                    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
                      ? undefined
                      : "Use a valid recipient email.",
                }}
              >
                {(field) => (
                  <Field data-invalid={field.state.meta.errors.length > 0}>
                    <FieldLabel htmlFor={field.name}>Recipient</FieldLabel>
                    <Input
                      id={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      placeholder="name@example.com"
                      aria-invalid={field.state.meta.errors.length > 0}
                    />
                    <FieldError errors={field.state.meta.errors} />
                  </Field>
                )}
              </form.Field>
              <form.Field
                name="subject"
                validators={{
                  onChange: ({ value }) =>
                    value.trim() ? undefined : "Subject is required.",
                }}
              >
                {(field) => (
                  <Field data-invalid={field.state.meta.errors.length > 0}>
                    <FieldLabel htmlFor={field.name}>Subject</FieldLabel>
                    <Input
                      id={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                      aria-invalid={field.state.meta.errors.length > 0}
                    />
                    <FieldError errors={field.state.meta.errors} />
                  </Field>
                )}
              </form.Field>
            </div>
            <form.Field
              name="body"
              validators={{
                onChange: ({ value }) =>
                  value.trim() ? undefined : "Body is required.",
              }}
            >
              {(field) => (
                <Field data-invalid={field.state.meta.errors.length > 0}>
                  <FieldLabel htmlFor={field.name}>Body</FieldLabel>
                  <Textarea
                    id={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(event) => field.handleChange(event.target.value)}
                    rows={6}
                    aria-invalid={field.state.meta.errors.length > 0}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.Field>
            <form.Field name="html">
              {(field) => (
                <Field orientation="horizontal">
                  <Checkbox
                    checked={field.state.value}
                    onCheckedChange={(checked) =>
                      field.handleChange(checked === true)
                    }
                  />
                  <FieldLabel>Send body as HTML</FieldLabel>
                </Field>
              )}
            </form.Field>
            <form.Subscribe
              selector={(state) => [
                state.canSubmit,
                state.isSubmitting,
                state.values.to,
                state.values.subject,
                state.values.body,
              ]}
            >
              {([canSubmit, isSubmitting, to, subject, body]) => {
                const toValue = String(to);
                const subjectValue = String(subject);
                const bodyValue = String(body);
                return (
                  <Button
                    type="submit"
                    disabled={
                      !canSubmit ||
                      Boolean(isSubmitting) ||
                      !toValue.trim() ||
                      !subjectValue.trim() ||
                      !bodyValue.trim()
                    }
                  >
                    <SendIcon data-icon="inline-start" />
                    {isSubmitting ? "Queueing..." : "Send test"}
                  </Button>
                );
              }}
            </form.Subscribe>
            {status ? (
              <Alert>
                <AlertDescription>{status}</AlertDescription>
              </Alert>
            ) : null}
          </FieldGroup>
        </form>
      </CardContent>
    </Card>
  );
}

function UsersTable({ users }: { users: DashboardUser[] }) {
  return (
    <Card className="shadow-none">
      <CardHeader>
        <CardTitle>Users</CardTitle>
        <CardDescription>Accounts, keys, and recorded activity</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        <TableFrame className="hidden md:block">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[34%]">User</TableHead>
                <TableHead className="w-[16%]">Role</TableHead>
                <TableHead className="w-[10%]">Keys</TableHead>
                <TableHead className="w-[14%]">Requests</TableHead>
                <TableHead className="w-[12%]">Emails</TableHead>
                <TableHead className="w-[14%]">Last seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="min-w-0">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{user.email}</p>
                      <p className="text-sm text-muted-foreground">
                        {user.name ?? "Unnamed"}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      tone={user.status === "active" ? "good" : "bad"}
                    >
                      {user.role}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>{formatCount(user.apiKeyCount)}</TableCell>
                  <TableCell>{formatCount(user.requestCount)}</TableCell>
                  <TableCell>{formatCount(user.emailCount)}</TableCell>
                  <TableCell>{formatDate(user.lastSeenAt)}</TableCell>
                </TableRow>
              ))}
              {users.length === 0 ? (
                <EmptyTableRow colSpan={6}>No users recorded.</EmptyTableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableFrame>
        <div className="grid gap-2 md:hidden">
          {users.map((user) => (
            <div
              className="grid gap-3 rounded-lg border bg-background p-3"
              key={user.id}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{user.email}</p>
                  <p className="truncate text-sm text-muted-foreground">
                    {user.name ?? "Unnamed"}
                  </p>
                </div>
                <StatusBadge tone={user.status === "active" ? "good" : "bad"}>
                  {user.role}
                </StatusBadge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Keys</p>
                  <p className="font-medium">{formatCount(user.apiKeyCount)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Requests</p>
                  <p className="font-medium">{formatCount(user.requestCount)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Emails</p>
                  <p className="font-medium">{formatCount(user.emailCount)}</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Last seen {formatDate(user.lastSeenAt)}
              </p>
            </div>
          ))}
          {users.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users recorded.</p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function CopyButton({
  value,
  label = "Copy",
  children,
  className,
  size = "icon-sm",
  variant = "ghost",
}: {
  value: string;
  label?: string;
  children?: React.ReactNode;
  className?: string;
  size?: React.ComponentProps<typeof Button>["size"];
  variant?: React.ComponentProps<typeof Button>["variant"];
}) {
  const [copied, setCopied] = useState(false);

  async function copy(event: React.MouseEvent) {
    event.stopPropagation();
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  }

  return (
    <Button
      aria-label={label}
      disabled={!value}
      className={className}
      onClick={copy}
      size={size}
      variant={variant}
    >
      {children ? (
        copied ? (
          <>
            <CheckIcon data-icon="inline-start" />
            Copied
          </>
        ) : (
          children
        )
      ) : copied ? (
        <CheckIcon />
      ) : (
        <CopyIcon />
      )}
    </Button>
  );
}

function ApiKeysTable({
  apiKeys,
  renewedKey,
  pendingKeyActionId,
  onRevoke,
  onRenew,
}: {
  apiKeys: DashboardApiKey[];
  renewedKey: RenewApiKeyResponse | null;
  pendingKeyActionId?: string;
  onRevoke: (apiKey: DashboardApiKey) => void;
  onRenew: (apiKey: DashboardApiKey) => void;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "active" | "revoked" | "expired">(
    "all",
  );
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const now = Date.now();
  const filtered = apiKeys.filter((apiKey) => {
    const expired = Boolean(apiKey.expiresAt && new Date(apiKey.expiresAt).getTime() < now);
    const revoked = Boolean(apiKey.revokedAt);
    const active = !expired && !revoked;
    const matchesFilter =
      filter === "all" ||
      (filter === "active" && active) ||
      (filter === "revoked" && revoked) ||
      (filter === "expired" && expired);
    return matchesFilter && matchesSearch([
      apiKey.id,
      apiKey.name,
      apiKey.keyPrefix,
      apiKey.userEmail,
      apiKey.userName,
      apiKey.userRole,
      apiKey.scopes.join(" "),
    ], query);
  });

  function keyTone(apiKey: DashboardApiKey): "good" | "warn" | "neutral" | "bad" {
    if (apiKey.revokedAt) return "bad";
    if (apiKey.expiresAt && new Date(apiKey.expiresAt).getTime() < now) return "warn";
    return "good";
  }

  function keyStatus(apiKey: DashboardApiKey) {
    if (apiKey.revokedAt) return "revoked";
    if (apiKey.expiresAt && new Date(apiKey.expiresAt).getTime() < now) return "expired";
    return "active";
  }

  function displayPrefix(apiKey: DashboardApiKey) {
    if (visible[apiKey.id]) return apiKey.keyPrefix;
    return `${apiKey.keyPrefix.slice(0, 4)}****${apiKey.keyPrefix.slice(-2)}`;
  }

  function exportLine(apiKey: DashboardApiKey) {
    return [
      `owner=${apiKey.userEmail}`,
      `name=${apiKey.name}`,
      `status=${keyStatus(apiKey)}`,
      `prefix=${apiKey.keyPrefix}`,
      `id=${apiKey.id}`,
      `scopes=${apiKey.scopes.join("|")}`,
      `lastUsed=${apiKey.lastUsedAt ?? "never"}`,
    ].join("\t");
  }

  const visibleExport = filtered.map(exportLine).join("\n");

  return (
    <Card className="shadow-none">
      <CardHeader className="gap-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-col gap-1">
            <CardTitle>API keys</CardTitle>
            <CardDescription>
              Safe key inventory for service/admin review. Full secrets are only shown once at creation.
            </CardDescription>
          </div>
          <CopyButton
            value={visibleExport}
            label="Copy visible API keys"
            size="sm"
            variant="outline"
          >
            <CopyIcon data-icon="inline-start" />
            Copy visible
          </CopyButton>
        </div>
        <div className="flex min-w-0 flex-col gap-2 xl:flex-row xl:items-center">
          <TableSearch
            value={query}
            onChange={setQuery}
            placeholder="Search owner, key name, prefix, ID, or scope"
          />
          <ToggleGroup
            aria-label="API key filter"
            className="max-w-full overflow-x-auto rounded-md border bg-background p-0.5"
            onValueChange={(value) => {
              if (value) setFilter(value as typeof filter);
            }}
            size="sm"
            spacing={0}
            type="single"
            value={filter}
          >
            <ToggleGroupItem value="all">All</ToggleGroupItem>
            <ToggleGroupItem value="active">Active</ToggleGroupItem>
            <ToggleGroupItem value="revoked">Revoked</ToggleGroupItem>
            <ToggleGroupItem value="expired">Expired</ToggleGroupItem>
          </ToggleGroup>
        </div>
        {renewedKey ? (
          <Alert>
            <AlertDescription className="grid gap-2">
              <span>Renewed {renewedKey.name}. Copy the new API key now.</span>
              <div className="flex min-w-0 items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded-md bg-muted px-2 py-1 font-mono text-xs">
                  {renewedKey.token}
                </code>
                <CopyButton
                  value={renewedKey.token.trim()}
                  label="Copy renewed API key"
                  size="sm"
                  variant="outline"
                >
                  <CopyIcon data-icon="inline-start" />
                  Copy API key
                </CopyButton>
              </div>
            </AlertDescription>
          </Alert>
        ) : null}
      </CardHeader>
      <CardContent className="grid gap-3">
        <TableFrame className="hidden md:block">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[25%]">Owner</TableHead>
                <TableHead className="w-[25%]">Key</TableHead>
                <TableHead className="w-[12%]">Status</TableHead>
                <TableHead className="hidden w-[20%] lg:table-cell">Scopes</TableHead>
                <TableHead className="hidden w-[10%] md:table-cell">Last used</TableHead>
                <TableHead className="w-[12%] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((apiKey) => (
                <TableRow key={apiKey.id}>
                  <TableCell className="min-w-0">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{apiKey.userEmail}</p>
                      <p className="truncate text-sm text-muted-foreground">
                        {apiKey.userName ?? apiKey.userRole}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="min-w-0">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{apiKey.name}</p>
                      <div className="flex min-w-0 items-center gap-1">
                        <code className="truncate rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs">
                          {displayPrefix(apiKey)}
                        </code>
                        <Button
                          aria-label={visible[apiKey.id] ? "Hide key prefix" : "Show key prefix"}
                          onClick={() =>
                            setVisible((current) => ({
                              ...current,
                              [apiKey.id]: !current[apiKey.id],
                            }))
                          }
                          size="icon-sm"
                          variant="ghost"
                        >
                          {visible[apiKey.id] ? <EyeOffIcon /> : <EyeIcon />}
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge tone={keyTone(apiKey)}>{keyStatus(apiKey)}</StatusBadge>
                  </TableCell>
                  <TableCell className="hidden truncate text-muted-foreground lg:table-cell">
                    {apiKey.scopes.join(", ")}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {formatDate(apiKey.lastUsedAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <CopyButton value={exportLine(apiKey)} label="Copy safe key line" />
                      <Button
                        aria-label="Renew API key"
                        disabled={
                          Boolean(apiKey.revokedAt) ||
                          pendingKeyActionId === apiKey.id
                        }
                        onClick={() => onRenew(apiKey)}
                        size="icon-sm"
                        variant="ghost"
                      >
                        <RefreshCwIcon />
                      </Button>
                      <Button
                        aria-label="Revoke API key"
                        disabled={
                          Boolean(apiKey.revokedAt) ||
                          pendingKeyActionId === apiKey.id
                        }
                        onClick={() => onRevoke(apiKey)}
                        size="icon-sm"
                        variant="ghost"
                      >
                        <PowerIcon />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 ? (
                <EmptyTableRow colSpan={6}>
                  No API keys match this filter.
                </EmptyTableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableFrame>
        <div className="grid gap-2 md:hidden">
          {filtered.map((apiKey) => (
            <div
              className="grid gap-3 rounded-lg border bg-background p-3"
              key={apiKey.id}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{apiKey.name}</p>
                  <p className="truncate text-sm text-muted-foreground">
                    {apiKey.userEmail}
                  </p>
                </div>
                <StatusBadge tone={keyTone(apiKey)}>
                  {keyStatus(apiKey)}
                </StatusBadge>
              </div>
              <div className="flex items-center justify-between gap-2">
                <code className="min-w-0 truncate rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs">
                  {displayPrefix(apiKey)}
                </code>
                <div className="flex shrink-0 gap-1">
                  <Button
                    aria-label={
                      visible[apiKey.id] ? "Hide key prefix" : "Show key prefix"
                    }
                    onClick={() =>
                      setVisible((current) => ({
                        ...current,
                        [apiKey.id]: !current[apiKey.id],
                      }))
                    }
                    size="icon-sm"
                    variant="ghost"
                  >
                    {visible[apiKey.id] ? <EyeOffIcon /> : <EyeIcon />}
                  </Button>
                  <CopyButton value={exportLine(apiKey)} label="Copy safe key line" />
                  <Button
                    aria-label="Renew API key"
                    disabled={
                      Boolean(apiKey.revokedAt) ||
                      pendingKeyActionId === apiKey.id
                    }
                    onClick={() => onRenew(apiKey)}
                    size="icon-sm"
                    variant="ghost"
                  >
                    <RefreshCwIcon />
                  </Button>
                  <Button
                    aria-label="Revoke API key"
                    disabled={
                      Boolean(apiKey.revokedAt) ||
                      pendingKeyActionId === apiKey.id
                    }
                    onClick={() => onRevoke(apiKey)}
                    size="icon-sm"
                    variant="ghost"
                  >
                    <PowerIcon />
                  </Button>
                </div>
              </div>
              <div className="grid gap-1 text-xs text-muted-foreground">
                <p className="truncate">Scopes: {apiKey.scopes.join(", ")}</p>
                <p>Last used: {formatDate(apiKey.lastUsedAt)}</p>
              </div>
            </div>
          ))}
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No API keys match this filter.
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function matchesSearch(
  values: Array<string | number | null | undefined>,
  query: string,
) {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return values.some((value) =>
    String(value ?? "")
      .toLowerCase()
      .includes(needle),
  );
}

function logTone(level: AppLog["level"]): "good" | "warn" | "neutral" | "bad" {
  if (level === "error") return "bad";
  if (level === "warn") return "warn";
  if (level === "info") return "good";
  return "neutral";
}

function DetailField({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  const displayValue =
    value === null || value === undefined || value === "" ? "None" : value;
  return (
    <div className="grid gap-1 rounded-lg border bg-background p-3">
      <span className="pixel-type text-[10.5px] text-faint">
        {label}
      </span>
      <span
        className={mono ? "break-all font-mono text-xs" : "break-words text-sm"}
      >
        {displayValue}
      </span>
    </div>
  );
}

function MetadataBlock({ value }: { value: Record<string, unknown> }) {
  const content = Object.keys(value).length
    ? JSON.stringify(value, null, 2)
    : "No metadata recorded.";
  return (
    <pre className="max-h-72 overflow-auto rounded-lg border bg-muted/30 p-3 text-xs leading-relaxed">
      {content}
    </pre>
  );
}

function TableSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative min-w-0 flex-1">
      <SearchIcon
        aria-hidden="true"
        className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        className="w-full pl-8"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function useLoadMoreSentinel(canLoadMore: boolean, onLoadMore: () => void) {
  const markerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !canLoadMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { rootMargin: "240px" },
    );
    observer.observe(marker);
    return () => observer.disconnect();
  }, [canLoadMore, onLoadMore]);

  return markerRef;
}

function LoadMoreMarker({
  canLoadMore,
  onLoadMore,
}: {
  canLoadMore: boolean;
  onLoadMore: () => void;
}) {
  const markerRef = useLoadMoreSentinel(canLoadMore, onLoadMore);
  return (
    <div className="flex justify-center pt-2" ref={markerRef}>
      {canLoadMore ? (
        <Button size="sm" variant="outline" onClick={onLoadMore}>
          Load more
        </Button>
      ) : null}
    </div>
  );
}

function UsageDetailDialog({
  event,
  onClose,
}: {
  event: UsageEvent | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(event)} onOpenChange={(open) => !open && onClose()}>
      <DetailDialogContent>
        {event ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {event.method} {event.path}
              </DialogTitle>
              <DialogDescription>
                Request details, API key context, and error metadata.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 sm:grid-cols-2">
              <DetailField label="Status" value={event.statusCode} />
              <DetailField label="Duration" value={`${event.durationMs}ms`} />
              <DetailField
                label="Auth method"
                value={event.authMethod ?? "unknown"}
              />
              <DetailField
                label="Created"
                value={formatDate(event.createdAt)}
              />
              <DetailField label="Request ID" mono value={event.requestId} />
              <DetailField
                label="API key ID"
                mono
                value={event.apiKeyId ?? "None"}
              />
              <DetailField
                label="User ID"
                mono
                value={event.userId ?? "None"}
              />
              <DetailField
                label="Error code"
                mono
                value={event.errorCode ?? "None"}
              />
              <DetailField label="IP" mono value={event.ip ?? "None"} />
              <DetailField
                label="User agent"
                value={event.userAgent ?? "None"}
              />
            </div>
            <DialogFooter showCloseButton />
          </>
        ) : null}
      </DetailDialogContent>
    </Dialog>
  );
}

function EmailDetailDialog({
  event,
  onClose,
}: {
  event: EmailEvent | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(event)} onOpenChange={(open) => !open && onClose()}>
      <DetailDialogContent>
        {event ? (
          <>
            <DialogHeader>
              <DialogTitle>{event.subject}</DialogTitle>
              <DialogDescription>
                Email event details, provider state, and resend context.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 sm:grid-cols-2">
              <DetailField label="Recipient" value={event.to} />
              <DetailField label="Status" value={event.status} />
              <DetailField
                label="Provider"
                value={event.provider ?? "pending"}
              />
              <DetailField
                label="Created"
                value={formatDate(event.createdAt)}
              />
              <DetailField
                label="Request ID"
                mono
                value={event.requestId ?? "None"}
              />
              <DetailField label="Job ID" mono value={event.jobId ?? "None"} />
              <DetailField
                label="API key ID"
                mono
                value={event.apiKeyId ?? "None"}
              />
              <DetailField
                label="User ID"
                mono
                value={event.userId ?? "None"}
              />
              <DetailField
                label="Message ID"
                mono
                value={event.messageId ?? "None"}
              />
              <DetailField label="Error" value={event.error ?? "None"} />
            </div>
            <div className="grid gap-2">
              <span className="pixel-type text-[10.5px] text-faint">
                Metadata
              </span>
              <MetadataBlock value={event.metadata} />
            </div>
            <DialogFooter showCloseButton />
          </>
        ) : null}
      </DetailDialogContent>
    </Dialog>
  );
}

function LogDetailDialog({
  log,
  onClose,
}: {
  log: AppLog | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(log)} onOpenChange={(open) => !open && onClose()}>
      <DetailDialogContent>
        {log ? (
          <>
            <DialogHeader>
              <DialogTitle>{log.message}</DialogTitle>
              <DialogDescription>
                Log details, request context, API key/user linkage where
                available.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 sm:grid-cols-2">
              <DetailField label="Level" value={log.level} />
              <DetailField label="Created" value={formatDate(log.createdAt)} />
              <DetailField label="Path" value={log.path ?? "None"} />
              <DetailField
                label="Request ID"
                mono
                value={log.requestId ?? "None"}
              />
              <DetailField label="User ID" mono value={log.userId ?? "None"} />
            </div>
            <div className="grid gap-2">
              <span className="pixel-type text-[10.5px] text-faint">
                Metadata
              </span>
              <MetadataBlock value={log.metadata} />
            </div>
            <DialogFooter showCloseButton />
          </>
        ) : null}
      </DetailDialogContent>
    </Dialog>
  );
}

function UsageTable({
  usage,
  canLoadMore,
  onLoadMore,
}: {
  usage: UsageEvent[];
  canLoadMore: boolean;
  onLoadMore: () => void;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<
    "all" | "errors" | "success" | "api_key"
  >("all");
  const [selected, setSelected] = useState<UsageEvent | null>(null);
  const filtered = usage.filter((event) => {
    const matchesFilter =
      filter === "all" ||
      (filter === "errors" && event.statusCode >= 400) ||
      (filter === "success" && event.statusCode < 400) ||
      (filter === "api_key" && event.authMethod === "api_key");
    return (
      matchesFilter &&
      matchesSearch(
        [
          event.method,
          event.path,
          event.statusCode,
          event.requestId,
          event.apiKeyId,
          event.userId,
          event.errorCode,
        ],
        query,
      )
    );
  });

  return (
    <Card className="shadow-none">
      <CardHeader className="gap-4">
        <div className="flex flex-col gap-1">
          <CardTitle>API usage</CardTitle>
          <CardDescription>
            {filtered.length} of {usage.length} loaded request records
          </CardDescription>
        </div>
        <div className="flex min-w-0 flex-col gap-2 lg:flex-row lg:items-center">
          <TableSearch
            value={query}
            onChange={setQuery}
            placeholder="Search route, request, API key, or user"
          />
          <ToggleGroup
            aria-label="Usage filter"
            className="max-w-full overflow-x-auto rounded-md border bg-background p-0.5"
            onValueChange={(value) => {
              if (value) setFilter(value as typeof filter);
            }}
            size="sm"
            spacing={0}
            type="single"
            value={filter}
          >
            <ToggleGroupItem value="all">All</ToggleGroupItem>
            <ToggleGroupItem value="errors">Errors</ToggleGroupItem>
            <ToggleGroupItem value="success">OK</ToggleGroupItem>
            <ToggleGroupItem value="api_key">API key</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </CardHeader>
      <CardContent className="grid gap-3">
        <TableFrame className="hidden md:block">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[42%]">Request</TableHead>
                <TableHead className="w-[12%]">Status</TableHead>
                <TableHead className="w-[16%]">Auth</TableHead>
                <TableHead className="w-[18%]">API key / user</TableHead>
                <TableHead className="w-[12%] text-right">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((event) => (
                <TableRow
                  className="cursor-pointer"
                  key={event.id}
                  onClick={() => setSelected(event)}
                >
                  <TableCell className="min-w-0">
                    <div className="min-w-0">
                      <p className="font-medium">{event.method}</p>
                      <p className="truncate text-sm text-muted-foreground">
                        {event.path}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      tone={event.statusCode >= 400 ? "bad" : "good"}
                    >
                      {event.statusCode}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>{event.authMethod ?? "unknown"}</TableCell>
                  <TableCell className="min-w-0">
                    <p className="truncate font-mono text-xs">
                      {event.apiKeyId ?? event.userId ?? "None"}
                    </p>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDate(event.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 ? (
                <EmptyTableRow colSpan={5}>
                  No usage events match this filter.
                </EmptyTableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableFrame>
        <div className="grid gap-2 md:hidden">
          {filtered.map((event) => (
            <button
              className="grid gap-2 rounded-lg border bg-background p-3 text-left"
              key={event.id}
              onClick={() => setSelected(event)}
              type="button"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium">{event.method}</span>
                <StatusBadge tone={event.statusCode >= 400 ? "bad" : "good"}>
                  {event.statusCode}
                </StatusBadge>
              </div>
              <span className="truncate text-sm text-muted-foreground">
                {event.path}
              </span>
              <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                <span>{event.authMethod ?? "unknown"}</span>
                <span>{formatDate(event.createdAt)}</span>
              </div>
            </button>
          ))}
        </div>
        <LoadMoreMarker canLoadMore={canLoadMore} onLoadMore={onLoadMore} />
        <UsageDetailDialog event={selected} onClose={() => setSelected(null)} />
      </CardContent>
    </Card>
  );
}

function ActivityTable({
  emailEvents,
  canLoadMore,
  onLoadMore,
  onResend,
  resendingId,
}: {
  emailEvents: EmailEvent[];
  canLoadMore: boolean;
  onLoadMore: () => void;
  onResend: (event: EmailEvent) => void;
  resendingId?: string;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<EmailEvent["status"] | "all">("all");
  const [selected, setSelected] = useState<EmailEvent | null>(null);
  const filtered = emailEvents.filter((event) => {
    const matchesFilter = filter === "all" || event.status === filter;
    return (
      matchesFilter &&
      matchesSearch(
        [
          event.to,
          event.subject,
          event.status,
          event.provider,
          event.error,
          event.requestId,
          event.jobId,
          event.apiKeyId,
          event.userId,
        ],
        query,
      )
    );
  });

  function resend(event: EmailEvent, clickEvent: React.MouseEvent) {
    clickEvent.stopPropagation();
    onResend(event);
  }

  return (
    <Card className="shadow-none">
      <CardHeader className="gap-4">
        <div className="flex flex-col gap-1">
          <CardTitle>Email events</CardTitle>
          <CardDescription>
            {filtered.length} of {emailEvents.length} loaded queue and delivery
            records
          </CardDescription>
        </div>
        <div className="flex min-w-0 flex-col gap-2 xl:flex-row xl:items-center">
          <TableSearch
            value={query}
            onChange={setQuery}
            placeholder="Search recipient, subject, error, API key, or user"
          />
          <ToggleGroup
            aria-label="Email event filter"
            className="max-w-full overflow-x-auto rounded-md border bg-background p-0.5"
            onValueChange={(value) => {
              if (value) setFilter(value as typeof filter);
            }}
            size="sm"
            spacing={0}
            type="single"
            value={filter}
          >
            <ToggleGroupItem value="all">All</ToggleGroupItem>
            <ToggleGroupItem value="failed">Failed</ToggleGroupItem>
            <ToggleGroupItem value="processing">Processing</ToggleGroupItem>
            <ToggleGroupItem value="queued">Queued</ToggleGroupItem>
            <ToggleGroupItem value="sent">Sent</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </CardHeader>
      <CardContent className="grid gap-3">
        <TableFrame className="hidden lg:block">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[28%]">Message</TableHead>
                <TableHead className="w-[10%]">Status</TableHead>
                <TableHead className="w-[10%]">Provider</TableHead>
                <TableHead className="w-[15%]">API key / user</TableHead>
                <TableHead className="w-[20%]">Error</TableHead>
                <TableHead className="w-[10%] text-right">Created</TableHead>
                <TableHead className="w-[7%] text-right">Control</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((event) => (
                <TableRow
                  className="cursor-pointer"
                  key={event.id}
                  onClick={() => setSelected(event)}
                >
                  <TableCell className="min-w-0">
                    <div className="min-w-0">
                      <p className="truncate font-medium">
                        {event.to}
                      </p>
                      <p className="truncate text-sm text-muted-foreground">
                        {event.subject}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge tone={emailStatusTone(event.status)}>
                      {event.status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>{event.provider ?? "pending"}</TableCell>
                  <TableCell className="min-w-0">
                    <p className="truncate font-mono text-xs">
                      {event.apiKeyId ?? event.userId ?? "None"}
                    </p>
                  </TableCell>
                  <TableCell className="truncate text-muted-foreground">
                    {event.error ?? "None"}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDate(event.createdAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={
                        event.status !== "failed" || resendingId === event.id
                      }
                      onClick={(clickEvent) => resend(event, clickEvent)}
                    >
                      <RepeatIcon data-icon="inline-start" />
                      {resendingId === event.id ? "Retrying..." : "Resend"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 ? (
                <EmptyTableRow colSpan={7}>
                  No email events match this filter.
                </EmptyTableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableFrame>
        <div className="grid gap-2 lg:hidden">
          {filtered.map((event) => (
            <div
              className="grid gap-3 rounded-lg border bg-background p-3 text-left transition-colors hover:bg-muted/30"
              key={event.id}
              onClick={() => setSelected(event)}
              onKeyDown={(keyEvent) => {
                if (keyEvent.key === "Enter" || keyEvent.key === " ") {
                  keyEvent.preventDefault();
                  setSelected(event);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="grid min-w-0 gap-1">
                <p className="break-words text-sm font-medium leading-5">
                  {event.to}
                </p>
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <p className="truncate text-sm text-muted-foreground">
                    {event.subject}
                  </p>
                  <StatusBadge tone={emailStatusTone(event.status)}>
                    {event.status}
                  </StatusBadge>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>{event.provider ?? "pending"}</span>
                <span>{formatDate(event.createdAt)}</span>
              </div>
              {event.error ? (
                <p className="line-clamp-2 text-sm text-muted-foreground">
                  {event.error}
                </p>
              ) : null}
              <Button
                className="justify-self-start"
                size="sm"
                variant="outline"
                disabled={event.status !== "failed" || resendingId === event.id}
                onClick={(clickEvent) => resend(event, clickEvent)}
              >
                <RepeatIcon data-icon="inline-start" />
                {resendingId === event.id ? "Retrying..." : "Resend"}
              </Button>
            </div>
          ))}
        </div>
        <LoadMoreMarker canLoadMore={canLoadMore} onLoadMore={onLoadMore} />
        <EmailDetailDialog event={selected} onClose={() => setSelected(null)} />
      </CardContent>
    </Card>
  );
}

function LogsPanel({
  logs,
  canLoadMore,
  onLoadMore,
}: {
  logs: AppLog[];
  canLoadMore: boolean;
  onLoadMore: () => void;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<AppLog["level"] | "all">("all");
  const [selected, setSelected] = useState<AppLog | null>(null);
  const filtered = logs.filter((log) => {
    const matchesFilter = filter === "all" || log.level === filter;
    return (
      matchesFilter &&
      matchesSearch(
        [
          log.level,
          log.message,
          log.path,
          log.requestId,
          log.userId,
          JSON.stringify(log.metadata),
        ],
        query,
      )
    );
  });

  return (
    <Card className="shadow-none">
      <CardHeader className="gap-4">
        <div className="flex flex-col gap-1">
          <CardTitle>System logs</CardTitle>
          <CardDescription>
            {filtered.length} of {logs.length} loaded operational signals
          </CardDescription>
        </div>
        <div className="flex min-w-0 flex-col gap-2 lg:flex-row lg:items-center">
          <TableSearch
            value={query}
            onChange={setQuery}
            placeholder="Search message, path, request, user, or metadata"
          />
          <ToggleGroup
            aria-label="Log level filter"
            className="max-w-full overflow-x-auto rounded-md border bg-background p-0.5"
            onValueChange={(value) => {
              if (value) setFilter(value as typeof filter);
            }}
            size="sm"
            spacing={0}
            type="single"
            value={filter}
          >
            <ToggleGroupItem value="all">All</ToggleGroupItem>
            <ToggleGroupItem value="error">Error</ToggleGroupItem>
            <ToggleGroupItem value="warn">Warn</ToggleGroupItem>
            <ToggleGroupItem value="info">Info</ToggleGroupItem>
            <ToggleGroupItem value="debug">Debug</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </CardHeader>
      <CardContent className="grid gap-3">
        <TableFrame className="hidden md:block">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50%]">Signal</TableHead>
                <TableHead className="w-[20%]">Path</TableHead>
                <TableHead className="hidden w-[18%] lg:table-cell">
                  Request / user
                </TableHead>
                <TableHead className="w-[12%] text-right">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((log) => (
                <TableRow
                  className="cursor-pointer"
                  key={log.id}
                  onClick={() => setSelected(log)}
                >
                  <TableCell className="min-w-0">
                    <div className="flex min-w-0 items-center gap-3">
                      <StatusBadge tone={logTone(log.level)}>
                        {log.level}
                      </StatusBadge>
                      <span className="block min-w-0 truncate">{log.message}</span>
                    </div>
                  </TableCell>
                  <TableCell className="truncate text-muted-foreground">
                    {log.path ?? "None"}
                  </TableCell>
                  <TableCell className="hidden min-w-0 lg:table-cell">
                    <p className="truncate font-mono text-xs">
                      {log.requestId ?? log.userId ?? "None"}
                    </p>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDate(log.createdAt)}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 ? (
                <EmptyTableRow colSpan={4}>
                  No logs match this filter.
                </EmptyTableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableFrame>
        <div className="grid gap-2 md:hidden">
          {filtered.map((log) => (
            <button
              className="grid gap-2 rounded-lg border bg-background p-3 text-left transition-colors hover:bg-muted/30"
              key={log.id}
              onClick={() => setSelected(log)}
              type="button"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="min-w-0 line-clamp-2 text-sm font-medium leading-5">
                  {log.message}
                </p>
                <StatusBadge tone={logTone(log.level)}>{log.level}</StatusBadge>
              </div>
              <p className="truncate text-sm text-muted-foreground">
                {log.path ?? "No path"}
              </p>
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="min-w-0 truncate font-mono">
                  {log.requestId ?? log.userId ?? "No request"}
                </span>
                <span>{formatDate(log.createdAt)}</span>
              </div>
            </button>
          ))}
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No logs match this filter.
            </p>
          ) : null}
        </div>
        <LoadMoreMarker canLoadMore={canLoadMore} onLoadMore={onLoadMore} />
        <LogDetailDialog log={selected} onClose={() => setSelected(null)} />
      </CardContent>
    </Card>
  );
}

function LoadingDashboard() {
  return (
    <div className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <Skeleton className="h-72" />
    </div>
  );
}

function DashboardSidebar({
  session,
  manager,
  activePage,
}: {
  session: AuthSession;
  manager: boolean;
  activePage: DashboardPage;
}) {
  const { setOpenMobile } = useSidebar();
  const pages = dashboardPages.filter((page) => !page.managerOnly || manager);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild size="lg" tooltip="Rinci Hub">
              <a href={dashboardPagePath("overview")}>
                <BrandLogo />
                <span className="grid gap-1 text-left">
                  <span className="truncate font-medium">Rinci Hub</span>
                  <span className="pixel-type truncate text-[10px] text-faint">
                    Email API
                  </span>
                </span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="pixel-type text-[10px] text-faint">
            Dashboard
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {pages.map((page) => {
                const Icon = page.icon;
                return (
                  <SidebarMenuItem key={page.id}>
                    <SidebarMenuButton
                      asChild
                      isActive={activePage === page.id}
                      tooltip={page.label}
                    >
                      <a
                        aria-current={
                          activePage === page.id ? "page" : undefined
                        }
                        href={dashboardPagePath(page.id)}
                        onClick={() => setOpenMobile(false)}
                      >
                        <Icon />
                        <span>{page.label}</span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="p-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" tooltip={session.user.email}>
              <>
                <Avatar className="size-8 rounded-none after:rounded-none">
                  <AvatarFallback className="rounded-none font-mono text-xs">
                    {session.user.email.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="grid min-w-0 text-left">
                  <span className="truncate text-sm font-medium">
                    {session.user.email}
                  </span>
                  <span className="pixel-type truncate text-[10px] text-faint">
                    {session.user.role}
                  </span>
                </span>
              </>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

function Dashboard({
  session,
  onLogout,
}: {
  session: AuthSession;
  onLogout: () => void;
}) {
  const client = useMemo(() => api.withToken(session.token), [session.token]);
  const queryClient = useQueryClient();
  const manager = canManage(session);
  const [scope, setScope] = useState<DashboardScope>(manager ? "all" : "mine");
  const [usageLimit, setUsageLimit] = useState(tablePageSize);
  const [emailEventsLimit, setEmailEventsLimit] = useState(tablePageSize);
  const [logsLimit, setLogsLimit] = useState(tablePageSize);
  const [renewedKey, setRenewedKey] =
    useState<RenewApiKeyResponse | null>(null);
  const [activePage, setActivePage] = useState<DashboardPage>(() =>
    dashboardPageFromPath(window.location.hash),
  );
  const activePageMeta =
    dashboardPages.find((page) => page.id === activePage) ?? dashboardPages[0];

  useEffect(() => {
    function syncRoute() {
      setActivePage(dashboardPageFromPath(window.location.hash));
    }

    window.addEventListener("hashchange", syncRoute);
    syncRoute();
    return () => window.removeEventListener("hashchange", syncRoute);
  }, []);

  useEffect(() => {
    if (!activePageMeta.managerOnly || manager) return;
    window.location.hash = dashboardPagePath("overview");
  }, [activePageMeta.managerOnly, manager]);

  useEffect(() => {
    document.title = `${activePageMeta.label} | Rinci Hub`;
  }, [activePageMeta.label]);

  useEffect(() => {
    setUsageLimit(tablePageSize);
    setEmailEventsLimit(tablePageSize);
    setLogsLimit(tablePageSize);
  }, [scope]);

  const summaryQuery = useQuery({
    queryKey: ["dashboard", session.token, "summary", scope],
    queryFn: () => client.dashboard(scope),
    enabled: activePage === "overview",
  });
  const analyticsQuery = useQuery({
    queryKey: ["dashboard", session.token, "analytics", scope],
    queryFn: () => client.analytics(scope),
    enabled: activePage === "overview" || activePage === "analytics",
  });
  const providersQuery = useQuery({
    queryKey: ["dashboard", session.token, "providers"],
    queryFn: () => client.providers(),
    enabled: activePage === "settings",
  });
  const usageQuery = useQuery({
    queryKey: ["dashboard", session.token, "usage", scope, usageLimit],
    queryFn: () => client.usage(scope, usageLimit),
    enabled: activePage === "usage",
  });
  const emailEventsQuery = useQuery({
    queryKey: ["dashboard", session.token, "email-events", scope, emailEventsLimit],
    queryFn: () => client.emailEvents(scope, emailEventsLimit),
    enabled: activePage === "emails",
  });
  const logsQuery = useQuery({
    queryKey: ["dashboard", session.token, "logs", scope, logsLimit],
    queryFn: () => client.logs(scope, logsLimit),
    enabled: activePage === "logs",
  });
  const usersQuery = useQuery({
    queryKey: ["dashboard", session.token, "users"],
    queryFn: () => client.users(),
    enabled: activePage === "users" && manager,
  });
  const apiKeysQuery = useQuery({
    queryKey: ["dashboard", session.token, "api-keys"],
    queryFn: () => client.apiKeys(),
    enabled: activePage === "users" && manager,
  });
  const resendMutation = useMutation({
    mutationFn: (event: EmailEvent) => client.resendEmailEvent(event.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["dashboard", session.token],
      });
    },
  });
  const providersMutation = useMutation({
    mutationFn: (config: ProvidersConfigInput) =>
      client.updateProviders(config),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["dashboard", session.token],
      });
    },
  });
  const revokeApiKeyMutation = useMutation({
    mutationFn: (apiKey: DashboardApiKey) => client.revokeApiKey(apiKey.id),
    onSuccess: async () => {
      setRenewedKey(null);
      await queryClient.invalidateQueries({
        queryKey: ["dashboard", session.token],
      });
    },
  });
  const renewApiKeyMutation = useMutation({
    mutationFn: (apiKey: DashboardApiKey) => client.renewApiKey(apiKey.id),
    onSuccess: async (response) => {
      setRenewedKey(response);
      await queryClient.invalidateQueries({
        queryKey: ["dashboard", session.token],
      });
    },
  });

  const summary = summaryQuery.data ?? null;
  const analytics = analyticsQuery.data ?? null;
  const providers = providersQuery.data?.providers ?? [];
  const usage = usageQuery.data?.usage ?? [];
  const emailEvents = emailEventsQuery.data?.emailEvents ?? [];
  const logs = logsQuery.data?.logs ?? [];
  const users = usersQuery.data?.users ?? [];
  const apiKeys = apiKeysQuery.data?.apiKeys ?? [];
  const isFetching =
    summaryQuery.isFetching ||
    analyticsQuery.isFetching ||
    providersQuery.isFetching ||
    usageQuery.isFetching ||
    emailEventsQuery.isFetching ||
    logsQuery.isFetching ||
    usersQuery.isFetching ||
    apiKeysQuery.isFetching;
  const dashboardError = [
    summaryQuery.error,
    analyticsQuery.error,
    providersQuery.error,
    usageQuery.error,
    emailEventsQuery.error,
    logsQuery.error,
    usersQuery.error,
    apiKeysQuery.error,
    resendMutation.error,
    providersMutation.error,
    revokeApiKeyMutation.error,
    renewApiKeyMutation.error,
  ].find((queryError) => queryError instanceof Error);
  const error = dashboardError instanceof Error ? dashboardError.message : null;

  function updateScope(nextScope: DashboardScope) {
    if (nextScope === "all" && !manager) return;
    setScope(nextScope);
  }

  async function refresh() {
    await queryClient.invalidateQueries({
      queryKey: ["dashboard", session.token],
    });
  }

  function renderPage() {
    if (activePage === "overview") {
      if (summaryQuery.isLoading || analyticsQuery.isLoading)
        return <LoadingDashboard />;
      if (!summary || !analytics) return null;
      return (
        <>
          <Card className="pixel-grid overflow-hidden shadow-none">
            <CardHeader className="gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
              <div>
                <CardDescription className="pixel-type text-[10.5px] text-faint">
                  {scope === "all" ? "All workspace data" : "Current account"}
                </CardDescription>
                <CardTitle className="mt-3 max-w-[28ch] text-[28px] leading-tight font-medium tracking-tight text-balance">
                  {summary.totals.failedEmails24h > 0
                    ? `${formatCount(summary.totals.failedEmails24h)} failed ${
                        summary.totals.failedEmails24h === 1 ? "email" : "emails"
                      } in the last 24 hours.`
                    : "No failed emails in the last 24 hours."}
                </CardTitle>
              </div>
              <StatusBadge
                tone={summary.totals.failedEmails24h > 0 ? "bad" : "good"}
              >
                {summary.totals.failedEmails24h > 0 ? "attention" : "stable"}
              </StatusBadge>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 border-t border-line pt-4 md:grid-cols-3">
                <div className="grid gap-1">
                  <span className="pixel-type text-[10.5px] text-faint">
                    Request volume
                  </span>
                  <span className="text-lg font-medium tabular-nums">
                    {formatCount(summary.totals.requests24h)} in 24h
                  </span>
                </div>
                <div className="grid gap-1">
                  <span className="pixel-type text-[10.5px] text-faint">
                    Email creation
                  </span>
                  <span className="text-lg font-medium tabular-nums">
                    {formatCount(summary.totals.emails24h)} in 24h
                  </span>
                </div>
                <div className="grid gap-1">
                  <span className="pixel-type text-[10.5px] text-faint">
                    Failure rate
                  </span>
                  <span className="text-lg font-medium tabular-nums">
                    {formatPercent(analytics.totals.failureRate)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Requests"
              value={formatCount(summary.totals.requests)}
              hint={`${scope === "all" ? "All users" : "Your account"} all time`}
              icon={ActivityIcon}
            />
            <MetricCard
              label="24h requests"
              value={formatCount(summary.totals.requests24h)}
              hint="Recent protected API traffic"
              icon={Clock3Icon}
            />
            <MetricCard
              label="Emails"
              value={formatCount(summary.totals.emails)}
              hint={`${formatCount(summary.totals.emails24h)} created in 24h`}
              icon={MailIcon}
            />
            <MetricCard
              label="24h failures"
              value={formatCount(
                summary.totals.errors24h + summary.totals.failedEmails24h,
              )}
              hint="API errors and failed emails"
              icon={AlertTriangleIcon}
            />
          </section>
          <AnalyticsPanel analytics={analytics} />
        </>
      );
    }

    if (activePage === "analytics") {
      if (analyticsQuery.isLoading) return <LoadingDashboard />;
      return analytics ? <AnalyticsPanel analytics={analytics} /> : null;
    }
    if (activePage === "users") {
      if (!manager) return null;
      if (usersQuery.isLoading || apiKeysQuery.isLoading)
        return <LoadingDashboard />;
      return (
        <div className="grid gap-4">
          <UsersTable users={users} />
          <ApiKeysTable
            apiKeys={apiKeys}
            renewedKey={renewedKey}
            pendingKeyActionId={
              revokeApiKeyMutation.variables?.id ??
              renewApiKeyMutation.variables?.id
            }
            onRevoke={(apiKey) => revokeApiKeyMutation.mutate(apiKey)}
            onRenew={(apiKey) => renewApiKeyMutation.mutate(apiKey)}
          />
        </div>
      );
    }
    if (activePage === "emails") {
      if (emailEventsQuery.isLoading) return <LoadingDashboard />;
      return (
        <ActivityTable
          emailEvents={emailEvents}
          canLoadMore={emailEvents.length >= emailEventsLimit && emailEventsLimit < maxTableLimit}
          onLoadMore={() =>
            setEmailEventsLimit((limit) => Math.min(limit + tablePageSize, maxTableLimit))
          }
          onResend={(event) => resendMutation.mutate(event)}
          resendingId={resendMutation.variables?.id}
        />
      );
    }
    if (activePage === "usage") {
      if (usageQuery.isLoading) return <LoadingDashboard />;
      return (
        <UsageTable
          usage={usage}
          canLoadMore={usage.length >= usageLimit && usageLimit < maxTableLimit}
          onLoadMore={() =>
            setUsageLimit((limit) => Math.min(limit + tablePageSize, maxTableLimit))
          }
        />
      );
    }
    if (activePage === "logs") {
      if (logsQuery.isLoading) return <LoadingDashboard />;
      return (
        <LogsPanel
          logs={logs}
          canLoadMore={logs.length >= logsLimit && logsLimit < maxTableLimit}
          onLoadMore={() =>
            setLogsLimit((limit) => Math.min(limit + tablePageSize, maxTableLimit))
          }
        />
      );
    }

    if (providersQuery.isLoading) return <LoadingDashboard />;

    return (
      <section className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
        <div className="grid gap-4">
          {manager ? (
            <RegisterUserPanel
              client={client}
              disabled={!manager}
              onRegistered={refresh}
            />
          ) : null}
          <SendEmailPanel client={client} onSent={refresh} />
        </div>
        <div className="grid gap-4">
          <ProviderRail
            providers={providers}
            manager={manager}
            pending={providersMutation.isPending}
            onUpdate={(config) => providersMutation.mutate(config)}
          />
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle>Access</CardTitle>
              <CardDescription>Current authentication context</CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Manager access</span>
                <StatusBadge tone={manager ? "good" : "neutral"}>
                  {manager ? "enabled" : "limited"}
                </StatusBadge>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Scopes</span>
                <span className="text-right font-medium">
                  {session.scopes.join(", ")}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <ShieldCheckIcon data-icon="inline-start" />
                <span>Tokens remain in session storage only.</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <SidebarProvider>
      <DashboardSidebar
        activePage={activePage}
        manager={manager}
        session={session}
      />
      <SidebarInset>
        <header className="sticky top-0 z-10 border-b border-line bg-ink/85 px-3 py-3 backdrop-blur-md sm:px-5 lg:px-6">
          <div className="flex min-h-12 flex-wrap items-center justify-between gap-x-3 gap-y-2">
            <div className="flex min-w-0 flex-1 items-center gap-2.5">
              <SidebarTrigger className="shrink-0" />
              <div className="min-w-0">
                <ScrambleText
                  as="h1"
                  className="pixel-type block truncate text-[13px] leading-6 sm:text-[15px]"
                  key={activePageMeta.id}
                  text={activePageMeta.label}
                />
                <p className="hidden truncate text-xs text-dim md:block">
                  {activePageMeta.description}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <ScopeSwitch
                scope={scope}
                disabled={!manager}
                onChange={updateScope}
              />
              <Button
                aria-label="Refresh dashboard"
                disabled={isFetching}
                onClick={() => void refresh()}
                size="icon"
                variant="outline"
              >
                <RefreshCwIcon />
              </Button>
              <Button
                aria-label="Sign out"
                onClick={onLogout}
                size="icon"
                variant="ghost"
              >
                <LogOutIcon />
              </Button>
            </div>
          </div>
        </header>

        <section
          className="brand-in flex min-w-0 flex-col gap-4 p-3 sm:gap-5 sm:p-6 lg:p-8"
          key={activePage}
          style={brandDelay(40)}
        >
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          {renderPage()}
        </section>
      </SidebarInset>
    </SidebarProvider>
  );
}

// Signed out, the sign-in path and dashboard links show the sign-in form;
// anything else (the root, in-page anchors) shows the landing page.
function wantsSignIn(hash: string) {
  return (
    hash === signInPath ||
    dashboardPages.some((page) => hash === dashboardPagePath(page.id))
  );
}

function App() {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [booting, setBooting] = useState(true);
  const [hash, setHash] = useState(() => window.location.hash);

  useEffect(() => {
    function syncHash() {
      setHash(window.location.hash);
    }

    window.addEventListener("hashchange", syncHash);
    return () => window.removeEventListener("hashchange", syncHash);
  }, []);

  // The dashboard titles each page; signed out, go back to the plain name.
  useEffect(() => {
    if (!session) document.title = "Rinci Hub";
  }, [session]);

  useEffect(() => {
    const token = sessionStorage.getItem(sessionKey);
    if (!token) {
      setBooting(false);
      return;
    }
    const client = api.withToken(token);
    client
      .me()
      .then((result) => {
        setSession({
          token,
          user: result.user,
          scopes: result.auth.scopes,
        });
      })
      .catch(() => sessionStorage.removeItem(sessionKey))
      .finally(() => setBooting(false));
  }, []);

  if (booting) {
    return (
      <main className="flex min-h-[100dvh] flex-col items-center justify-center gap-5 bg-ink p-6">
        <PixelLogo label="Rinci Hub" mark width={36} />
        <p className="pixel-type text-[11px] text-faint" role="status">
          Loading your saved session
        </p>
      </main>
    );
  }

  if (!session) {
    if (!wantsSignIn(hash)) return <Landing />;
    return (
      <LoginPanel
        onLogin={(nextSession) => {
          if (window.location.hash === signInPath) {
            window.history.replaceState(null, "", dashboardPagePath("overview"));
          }
          setSession(nextSession);
        }}
      />
    );
  }

  return (
    <Dashboard
      session={session}
      onLogout={() => {
        sessionStorage.removeItem(sessionKey);
        window.history.replaceState(null, "", signInPath);
        setHash(signInPath);
        setSession(null);
      }}
    />
  );
}

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <TooltipProvider>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </TooltipProvider>
  </React.StrictMode>,
);
